import { useState, useMemo, useEffect, useCallback } from 'react';

import { Search, X, Download } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { CallRowWithHistory } from '@/components/calls/CallRowWithHistory';
import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useCallListSocket } from '@/hooks/useCallListSocket';
import { useCalls, useDeleteCall } from '@/hooks/useCalls';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useConfigurations } from '@/hooks/useConfigurations';
import { useEffectiveOutcomes } from '@/hooks/useOutcomes';
import { usePageTitle } from '@/hooks/usePageTitle';
import { getEnv } from '@/lib/env';
import { useAuthStore } from '@/stores/auth.store';

import {
  callStatusSchema,
  callDirectionSchema,
  callConsentFilterSchema,
  callTransportSchema,
  KNOWN_CALL_OUTCOMES,
} from '@repo/shared-types';

import { ExportCallsModal } from './ExportCallsModal';

import type {
  CallWithContact,
  CallQuery,
  CallStatus,
  CallDirection,
  CallOutcome,
  CallConsentFilter,
  CallTransport,
} from '@repo/shared-types';

const STATUSES = callStatusSchema.options;
const DIRECTIONS = callDirectionSchema.options;
const TRANSPORTS = callTransportSchema.options;
const TRANSPORT_LABELS: Record<CallTransport, string> = {
  PSTN: 'Phone',
  WEBRTC: 'Web',
  WHATSAPP: 'WhatsApp',
  WEB_WIDGET: 'Widget',
  EMAIL: 'Email',
};
// Static fallback while the dynamic outcome definitions load.
const FALLBACK_OUTCOMES = [...KNOWN_CALL_OUTCOMES];
const CONSENT_OPTIONS = callConsentFilterSchema.options;
const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10 records' },
  { value: 25, label: '25 records' },
  { value: 50, label: '50 records' },
  { value: 100, label: '100 records' },
  { value: null, label: 'All records' },
];

export const CallsPage = () => {
  usePageTitle('Calls');
  useCallListSocket();
  // Clients view: read-only, campaigns endpoint is off-limits
  const isClientView = useAuthStore((s) => s.user?.role === 'client');
  const [searchParams, setSearchParams] = useSearchParams();

  // Dynamic outcome vocabulary (outcome_definitions) — falls back to the
  // legacy static list until loaded.
  const { data: outcomeDefinitions } = useEffectiveOutcomes();
  const outcomeOptions = useMemo(
    () =>
      outcomeDefinitions && outcomeDefinitions.length > 0
        ? outcomeDefinitions.map((d) => ({ value: d.key, label: d.label }))
        : FALLBACK_OUTCOMES.map((o) => ({ value: o, label: o.replace(/_/g, ' ') })),
    [outcomeDefinitions],
  );

  // Read state from URL
  const status = (searchParams.get('status') as CallStatus) || '';
  const direction = (searchParams.get('direction') as CallDirection) || '';
  const callTransport = (searchParams.get('call_transport') as CallTransport) || '';
  const outcome = (searchParams.get('outcome') as CallOutcome) || '';
  const consent = (searchParams.get('consent') as CallConsentFilter) || '';
  const campaignId = searchParams.get('campaign_id') || '';
  const configurationId = searchParams.get('configuration_id') || '';
  const attemptNumber = searchParams.get('attemptNumber') || '';

  // Get date strings directly from URL (YYYY-MM-DD format)
  const executedAtFrom = searchParams.get('executedAtFrom') || '';
  const executedAtTo = searchParams.get('executedAtTo') || '';

  const page = Number(searchParams.get('page')) || 1;
  const limitParam = searchParams.get('limit');
  const limit = limitParam === 'all' ? undefined : Number(limitParam) || 10;

  // Fetch campaigns for filter dropdown
  const { data: campaignsData } = useCampaigns({ limit: 100 }, { enabled: !isClientView });

  // Fetch configurations for filter dropdown
  const { data: configurationsData } = useConfigurations();

  // Local state for search input (for debouncing)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

  // Update URL params helper
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Debounce search input and sync to URL
  useEffect(() => {
    const timer = setTimeout(() => {
      const searchChanged = debouncedSearch !== searchInput;
      setDebouncedSearch(searchInput);
      updateParams({
        search: searchInput || null,
        ...(searchChanged && { page: null }),
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, updateParams, debouncedSearch]);

  // Build query from state
  const query = useMemo(() => {
    const q: Partial<CallQuery> = { page };
    if (limit !== undefined) q.limit = limit;
    if (debouncedSearch) q.search = debouncedSearch;
    if (status) q.status = status;
    if (direction) q.direction = direction;
    if (callTransport) q.call_transport = callTransport;
    if (outcome) q.outcome = outcome;
    if (consent) q.consent = consent;
    if (campaignId) q.campaign_id = campaignId;
    if (configurationId) q.configuration_id = configurationId;
    if (attemptNumber) q.attemptNumber = Number(attemptNumber);
    if (executedAtFrom) {
      // Parse date string (YYYY-MM-DD) and create Date in local timezone at start of day
      const partsFrom = executedAtFrom.split('-');
      if (partsFrom.length === 3) {
        const [yearFrom, monthFrom, dayFrom] = partsFrom.map((part) => Number(part));
        if (!Number.isNaN(yearFrom) && !Number.isNaN(monthFrom) && !Number.isNaN(dayFrom)) {
          const fromDate = new Date(yearFrom, monthFrom - 1, dayFrom, 0, 0, 0, 0);
          if (!Number.isNaN(fromDate.getTime())) {
            q.executedAtFrom = fromDate;
          }
        }
      }
    }
    if (executedAtTo) {
      // Parse date string (YYYY-MM-DD) and create Date in local timezone at end of day
      const partsTo = executedAtTo.split('-');
      if (partsTo.length === 3) {
        const [yearTo, monthTo, dayTo] = partsTo.map((part) => Number(part));
        if (!Number.isNaN(yearTo) && !Number.isNaN(monthTo) && !Number.isNaN(dayTo)) {
          const toDate = new Date(yearTo, monthTo - 1, dayTo, 23, 59, 59, 999);
          if (!Number.isNaN(toDate.getTime())) {
            q.executedAtTo = toDate;
          }
        }
      }
    }
    return q;
  }, [
    debouncedSearch,
    status,
    direction,
    callTransport,
    outcome,
    consent,
    campaignId,
    configurationId,
    attemptNumber,
    executedAtFrom,
    executedAtTo,
    page,
    limit,
  ]);

  const { data, isLoading } = useCalls(query);
  const calls = data?.data ?? [];
  const pagination = data?.pagination;

  const deleteMutation = useDeleteCall();
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<CallWithContact | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters =
    searchInput ||
    status ||
    direction ||
    callTransport ||
    outcome ||
    consent ||
    campaignId ||
    configurationId ||
    executedAtFrom ||
    executedAtTo;

  const resolveContactName = (call: CallWithContact) => {
    // Priority: lead_data.customer_name > contact.name > contact_id > 'Unknown'
    const leadCustomerName = call.lead_data?.customer_name;
    return leadCustomerName || call.contact?.name || call.contact_id || 'Unknown';
  };

  const handleDeleteCall = (callId: string) => {
    const target = calls.find((call: CallWithContact) => call.id === callId);
    if (target) setDeleteTarget(target);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({
        variant: 'success',
        title: 'Call deleted',
        description: `Call ${deleteTarget.id} has been deleted successfully.`,
      });
    } catch (error) {
      console.error('Failed to delete call:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete call. Please try again.',
      });
    }
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Calls</p>
        <h1 className="text-3xl font-bold tracking-tight">AI agent call executions</h1>
        <p className="text-muted-foreground max-w-3xl">
          Contact attempts, including direction, status, outcome, captured availability, audio
          recordings, and transcript summaries.
        </p>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Calls</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportModalOpen(true)}
              disabled={calls.length === 0}
            >
              <Download className="h-4 w-4" />
              Export to Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="min-w-50 flex-1">
              <label className="text-muted-foreground mb-1 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by phone..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="focus:ring-primary w-full rounded-md border py-2 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
                />
              </div>
            </div>
            <div className="min-w-37.5">
              <label
                htmlFor="status-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Status
              </label>
              <select
                id="status-filter"
                value={status}
                onChange={(e) => updateParams({ status: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-37.5">
              <label
                htmlFor="direction-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Direction
              </label>
              <select
                id="direction-filter"
                value={direction}
                onChange={(e) => updateParams({ direction: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All directions</option>
                {DIRECTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-37.5">
              <label
                htmlFor="transport-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Channel
              </label>
              <select
                id="transport-filter"
                value={callTransport}
                onChange={(e) =>
                  updateParams({ call_transport: e.target.value || null, page: null })
                }
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All channels</option>
                {TRANSPORTS.map((t) => (
                  <option key={t} value={t}>
                    {TRANSPORT_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-45">
              <label
                htmlFor="outcome-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Outcome
              </label>
              <select
                id="outcome-filter"
                value={outcome}
                onChange={(e) => updateParams({ outcome: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All outcomes</option>
                {outcomeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') === 'NEW_CROSS' && (
              <div className="min-w-37.5">
                <label
                  htmlFor="consent-filter"
                  className="text-muted-foreground mb-1 block text-sm font-medium"
                >
                  Consent
                </label>
                <select
                  id="consent-filter"
                  value={consent}
                  onChange={(e) => updateParams({ consent: e.target.value || null, page: null })}
                  className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
                >
                  <option value="">All</option>
                  {CONSENT_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="min-w-45">
              <label
                htmlFor="campaign-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Campaign
              </label>
              <select
                id="campaign-filter"
                value={campaignId}
                onChange={(e) => updateParams({ campaign_id: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All campaigns</option>
                {campaignsData?.data.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-45">
              <label
                htmlFor="configuration-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Configuration
              </label>
              <select
                id="configuration-filter"
                value={configurationId}
                onChange={(e) =>
                  updateParams({ configuration_id: e.target.value || null, page: null })
                }
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All configurations</option>
                {configurationsData?.map((configuration) => (
                  <option key={configuration.id} value={configuration.id}>
                    {configuration.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-42.5">
              <label
                htmlFor="date-from-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                From Date
              </label>
              <input
                id="date-from-filter"
                type="date"
                value={executedAtFrom}
                onChange={(e) => {
                  const dateStr = e.target.value;
                  updateParams({
                    executedAtFrom: dateStr || null,
                    page: null,
                  });
                }}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              />
            </div>
            <div className="min-w-42.5">
              <label
                htmlFor="date-to-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                To Date
              </label>
              <input
                id="date-to-filter"
                type="date"
                value={executedAtTo}
                onChange={(e) => {
                  const dateStr = e.target.value;
                  updateParams({
                    executedAtTo: dateStr || null,
                    page: null,
                  });
                }}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              />
            </div>
            <div className="min-w-10">
              <label
                htmlFor="attempt-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Attempt #
              </label>
              <input
                id="attempt-filter"
                type="number"
                min="1"
                placeholder="Ex: 1"
                value={attemptNumber}
                onChange={(e) =>
                  updateParams({ attemptNumber: e.target.value || null, page: null })
                }
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              />
            </div>
            <div className="min-w-[120px]">
              <label
                htmlFor="limit-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Per Page
              </label>
              <select
                id="limit-filter"
                value={limit === undefined ? 'all' : limit}
                onChange={(e) =>
                  updateParams({
                    limit: e.target.value === 'all' ? 'all' : e.target.value,
                    page: null,
                  })
                }
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option.value ?? 'all'} value={option.value ?? 'all'}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
                <p className="text-muted-foreground mt-2 text-sm">Loading calls...</p>
              </div>
            </div>
          ) : calls.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No calls found. {hasActiveFilters ? 'Try adjusting your filters.' : ''}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs tracking-wide uppercase">
                    <th className="w-10 border-b px-3 py-2" />
                    <th className="border-b px-3 py-2">Contact</th>
                    <th className="border-b px-3 py-2">Channel</th>
                    <th className="border-b px-3 py-2">Status</th>
                    <th className="border-b px-3 py-2">Campaign</th>
                    <th className="border-b px-3 py-2">Configuration</th>
                    <th className="border-b px-3 py-2">Duration</th>
                    <th className="border-b px-3 py-2">Executed at</th>
                    <th className="border-b px-3 py-2">Reviewed</th>
                    <th className="w-12 border-b px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call: CallWithContact) => (
                    <CallRowWithHistory
                      key={call.id}
                      call={call}
                      resolveContactName={resolveContactName}
                      onDelete={isClientView ? undefined : handleDeleteCall}
                      readOnly={isClientView}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && (
            <div className="mt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : null })}
                resourceName="calls"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        resourceName="call"
        resourceIdentifier={deleteTarget?.id}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <ExportCallsModal open={exportModalOpen} onOpenChange={setExportModalOpen} calls={calls} />
    </div>
  );
};
